import styled from '@emotion/styled'
import { Dimension } from '@pubstack/common/src/analytics/dimension'
import { AnalyticsQueryDashboard, MappedName, ServerSideMetricsByTime } from '@pubstack/common/src/analytics/query'
import { useAtom } from 'jotai'
import { parseAsStringLiteral, useQueryState } from 'nuqs'
import { FunctionComponent, useState } from 'react'
import { useAnalyticsQueryTanstack } from '~/api/analytics/analytics-query.api'
import { LazyVisible } from '~/components/LazyVisible'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useScopeCurrency } from '~/hooks/useScopeCurrency.hooks'
import { AnalyticsPage } from '~/modules/analytics/AnalyticsPage'
import { SortLink } from '~/modules/analytics/common/styled/SortLink'
import { SortSelect } from '~/modules/analytics/common/styled/SortSelect'
import { ServerSideBreakdown } from '~/modules/analytics/server-side/ServerSideBreakdown'
import { ServerSideTimeline } from '~/modules/analytics/server-side/ServerSideTimeline'
import { ServerSideTopRevenue } from '~/modules/analytics/server-side/ServerSideTopRevenue'
import { TRACKING_LABELS } from '~/modules/analytics/server-side/trackingLabels'
import { contextState } from '~/state'
import { filterContextDimByName } from '~/utils/analytics'
import { ServerSideDimensions } from '~/utils/serverSide'

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const Wrapper = styled.div`
  display: flex;
  align-items: center;
`

const DimensionSelector: FunctionComponent<{ options: SelectOptionProp<string>[]; currentDimension: string; onChange: (option: SelectOptionProp) => unknown }> = ({
  options,
  currentDimension,
  onChange,
}) => {
  const [sortOpen, setSortOpen] = useState<boolean>(false)

  return (
    <Wrapper>
      By :
      <SortSelect
        options={options}
        onChange={onChange}
        trigger={<SortLink open={sortOpen} label={currentDimension} icon={'chevron_down'} trackingLabel={TRACKING_LABELS.serverSideDimension.change} />}
        open={sortOpen}
        setOpen={setSortOpen}
      />
    </Wrapper>
  )
}

const DIMENSIONS: Dimension[] = ['tagId', 'adUnit', 'country', 'bidder', 'size']
export const BREAKDOWN_DIMENSIONS: Extract<Dimension, 'tagId' | 'adUnit' | 'bidder'>[] = ['tagId', 'adUnit', 'bidder'] as const
const SERVER_SIDE_DASHBOARD: AnalyticsQueryDashboard = { name: 'server-side', filterType: 'auctions' }
const DIMENSION_SELECTOR_OPTIONS: SelectOptionProp<string>[] = [
  { label: 'Overview', value: 'overview', trackingLabel: TRACKING_LABELS.serverSideDimension.overview },
  { label: 'Bidders', value: 'bidder', trackingLabel: TRACKING_LABELS.serverSideDimension.bidder },
  { label: 'Sites', value: 'tagId', trackingLabel: TRACKING_LABELS.serverSideDimension.sites },
  { label: 'Ad units', value: 'adUnit', trackingLabel: TRACKING_LABELS.serverSideDimension.adunit },
] as const

const timelineEmptyData: ServerSideMetricsByTime = {
  epoch: [],
  mappings: {},
  serverSideImpressionCount: [],
  serverSideImpressionCpmSum: [],
  serverSideBidRequestCount: [],
  serverSideBidResponseCount: [],
  serverSideBidTimeoutCount: [],
  serverSideBidCpmSum: [],
  serverSideAuctionCount: [],
}

export const ServerSidePage: FunctionComponent = () => {
  // Context
  const [context, setContext] = useAtom(contextState)

  const [currentDimension, setCurrentDimension] = useQueryState(
    'dimension',
    parseAsStringLiteral([...BREAKDOWN_DIMENSIONS, 'overview'])
      .withDefault('overview')
      .withOptions({ clearOnDefault: false, history: 'push' })
  )

  // API Hook
  const analyticsQueryTime = useAnalyticsQueryTanstack<ServerSideMetricsByTime>('server-side.metrics.by.time', DIMENSIONS, context, { dimension: currentDimension })

  const breadcrumbs = useBreadcrumbs()
  const currency = useScopeCurrency()

  const currentDimensionLabel = DIMENSION_SELECTOR_OPTIONS.find((d) => d.value === currentDimension)?.label || ''

  const onValueSelect = (name: MappedName, dimension: Dimension) => {
    setContext((context) => filterContextDimByName(context, name, dimension))
  }

  return (
    <AnalyticsPage
      filterDimensions={DIMENSIONS}
      dashboard={SERVER_SIDE_DASHBOARD}
      title={
        <>
          {breadcrumbs}
          <DimensionSelector
            currentDimension={currentDimensionLabel}
            options={DIMENSION_SELECTOR_OPTIONS}
            onChange={(dimension) => {
              setCurrentDimension(dimension.value as ServerSideDimensions)
            }}
          />
        </>
      }
      showMinorDataToggle={false}
    >
      <PageContent>
        <ServerSideTimeline
          dimension={currentDimension}
          currencySymbol={currency}
          rawData={analyticsQueryTime.data ?? timelineEmptyData}
          isLoading={analyticsQueryTime.isFetching}
          error={analyticsQueryTime.isError}
          empty={!analyticsQueryTime.isFetching && !analyticsQueryTime?.data?.epoch.length}
          onRefreshClick={() => {
            analyticsQueryTime.refetch()
          }}
          onMetricChange={() => {}}
        />
        {currentDimension === 'overview' && (
          <LazyVisible minHeight={400}>
            <ServerSideTopRevenue
              currencySymbol={currency}
              onRevenueChartSelect={onValueSelect}
              context={context}
              onMoreClick={(dimension) => {
                if ((BREAKDOWN_DIMENSIONS as Dimension[]).includes(dimension)) {
                  setCurrentDimension(dimension as ServerSideDimensions)
                }
              }}
            />
          </LazyVisible>
        )}
        {currentDimension !== 'overview' && BREAKDOWN_DIMENSIONS.includes(currentDimension) && (
          <LazyVisible minHeight={400}>
            <ServerSideBreakdown onRefreshClick={() => {}} context={context} currencySymbol={currency} dimension={currentDimension} dimensions={DIMENSIONS} />
          </LazyVisible>
        )}
      </PageContent>
    </AnalyticsPage>
  )
}
